import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Subtitle2, Spinner, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';
import { DocumentTable20Regular, Money20Regular, ReOrderDotsHorizontal20Regular } from '@fluentui/react-icons';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import * as ComponentFunctions from './VisitConcept.Form.Functions';
import VisitConceptTree from '../../VisitConcepts/Organism/VisitConceptTree';
import { useTranslation } from 'react-i18next';
import VisitFormConceptPayableListComponent from '../VisitFormConceptPayable/VisitFormConceptPayable.List.Component';

//styles
import "./VisitConcept.Form.Styles.css";

// El cuerpo no scrollea: los dos paneles divididos tienen su propio scroll.
const useVisitConceptDrawerStyles = makeStyles({
    body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflowY: 'hidden',
        '& > div': {
            flexGrow: 1,
            minHeight: 0,
        },
    },
});

const MIN_PCT = 20;
const MAX_PCT = 80;

const VisitConceptFormComponent = (props: ComponentFunctions.Props) => {
    const { isOpen, onClose, protocolId, visitId, patientId, visitConcepts, isLoading, isLoadingSuccess, failLoading, error } = props;
    const { t } = useTranslation();
    const drawerStyles = useVisitConceptDrawerStyles();

    const [topPct, setTopPct] = useState<number>(60);
    const containerRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef<boolean>(false);

    useEffect(() => {
        props.getVisitConcepts(
            protocolId, visitId, patientId
        );
    }, [isOpen, protocolId, visitId, patientId]);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!draggingRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const pct = ((e.clientY - rect.top) / rect.height) * 100;
            setTopPct(Math.max(MIN_PCT, Math.min(MAX_PCT, pct)));
        };
        const onUp = () => {
            if (draggingRef.current) {
                draggingRef.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);

    const startDrag = (e: React.MouseEvent) => {
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
    };

    const handleResetSplit = () => setTopPct(60);

    return (
        <GuideDrawer
            open={isOpen}
            size="medium"
            title={t('visitConceptForm.title')}
            subtitle={t('visitConceptForm.subtitle', 'Conceptos relacionados con la visita')}
            onDismiss={onClose}
            bodyClassName={drawerStyles.body}
            secondaryAction={{ label: t('visitConceptForm.close') }}
            testId="visit-concept-drawer"
        >
                <div className="splitContainer" ref={containerRef}>
                    <section
                        className="splitPane splitPane--top"
                        style={{ flexBasis: `${topPct}%` }}
                    >
                        <header className="splitPane-header">
                            <span className="splitPane-headerIcon">
                                <DocumentTable20Regular />
                            </span>
                            <Subtitle2 className="splitPane-title">
                                {t('visitConceptForm.visitConcepts', 'Conceptos de la visita')}
                            </Subtitle2>
                        </header>
                        <div className="splitPane-body">
                            {isLoading && (
                                <div className="splitPane-loading">
                                    <Spinner size="tiny" label={t('visitConceptForm.loading')} />
                                </div>
                            )}
                            {failLoading && error && (
                                <GuideMessageBar intent="error">
                                    <MessageBarBody>
                                        <MessageBarTitle>{t('common.error', 'Error')}</MessageBarTitle>
                                        {error.ErrorMessage}
                                    </MessageBarBody>
                                </GuideMessageBar>
                            )}
                            {isLoadingSuccess && visitConcepts && (
                                <VisitConceptTree ContractId={props.ContractId} visit={visitConcepts} />
                            )}
                        </div>
                    </section>

                    <div
                        className="splitDivider"
                        role="separator"
                        aria-orientation="horizontal"
                        aria-label={t('visitConceptForm.resize', 'Redimensionar')}
                        onMouseDown={startDrag}
                        onDoubleClick={handleResetSplit}
                        title={t('visitConceptForm.resizeHint', 'Arrastra para redimensionar · Doble click para restablecer')}
                    >
                        <span className="splitDivider-handle">
                            <ReOrderDotsHorizontal20Regular />
                        </span>
                    </div>

                    <section
                        className="splitPane splitPane--bottom"
                        style={{ flexBasis: `${100 - topPct}%` }}
                    >
                        <header className="splitPane-header">
                            <span className="splitPane-headerIcon splitPane-headerIcon--accent">
                                <Money20Regular />
                            </span>
                            <Subtitle2 className="splitPane-title">
                                {t('visitConceptForm.payableConcept', 'Conceptos pagables')}
                            </Subtitle2>
                        </header>
                        <div className="splitPane-body">
                            <VisitFormConceptPayableListComponent ContractId={props.ContractId} />
                        </div>
                    </section>
                </div>
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(VisitConceptFormComponent);
